import moment, { Moment } from 'moment';
import React, { useEffect, useState } from 'react';

import { makeStyles } from '@mui/styles';

import {
  PODShuffleErrorType,
  PODShuffleStatus,
} from '@vestahealthcare/common/enums';
import { translate } from '@vestahealthcare/common/i18n';
import {
  FilterSelectable,
  MemberView,
  PODShuffleMember,
  PaginationType,
} from '@vestahealthcare/common/models';
import { EMPTY } from '@vestahealthcare/common/utils/constants';

import { Chip, PanelInfo } from 'styleguide-v2';

import { BulkEditTable } from 'dash/src/pages/Ops/Members/BulkEditTable';

import { showGlobalError } from '../../../../../components/GlobalMessage';
import { getPODShuffleMembers } from '../../../../../services/PodServices';
import { PODShuffleResultsFilters } from './PODShuffleResultsFilterBar';

const PAGE_SIZE = 10;

const useStyles = makeStyles({
  container: {
    width: '100%',
  },
});

const uniq = (array: any[]) => [...new Set(array)];

interface Props {
  filters: PODShuffleResultsFilters;
  setLoading: (loading: boolean) => void;
  selection: number[];
  setSelection: (sel: number[]) => void;
  refresh: number;
  setHasResultsMembers: (hasResultsMembers: boolean) => void;
  allSelected: boolean;
  setAllSelected: (allSelected: boolean) => void;
  setPagination: (pagination: PaginationType | undefined) => void;
}

export const PODShuffleResultsTab = ({
  filters,
  setLoading: setOuterLoading,
  selection,
  setSelection,
  refresh,
  setHasResultsMembers,
  allSelected,
  setAllSelected,
  setPagination: setOuterPagination,
}: Props) => {
  const styles = useStyles();
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [members, setMembers] = useState<PODShuffleMember[]>([]);
  const [pagination, setPagination] = useState<PaginationType>();

  const selectedMonth = (filters.month?.value as FilterSelectable<Moment>)
    ?.value;
  const selectedPod = (filters.podId?.value as FilterSelectable<string>)?.value;
  const selectedStatus = (filters.status?.value as FilterSelectable<string>)
    ?.value;

  const fetchMembers = async () => {
    if (!selectedMonth) {
      setMembers([]);
      setPagination(undefined);
      return;
    }

    setLoading(true);
    setOuterLoading(true);
    try {
      const dateString = moment(selectedMonth).format('YYYY-MM-DD');
      const offset = page * pageSize;
      const { items, pagination } = await getPODShuffleMembers({
        date: dateString,
        podId: selectedPod ? +selectedPod : undefined,
        statusId: selectedStatus,
        offset,
        limit: pageSize,
      });

      if (offset !== 0) {
        setMembers([...members, ...items]);
      } else {
        setMembers(items);
      }
      setPagination({
        limit: pageSize,
        offset,
        total: pagination.total,
      });
      setOuterPagination({
        limit: pageSize,
        offset,
        total: pagination.total,
      });
    } catch (error) {
      showGlobalError(error as string);
      setMembers([]);
      setPagination(undefined);
      setOuterPagination(undefined);
    } finally {
      setLoading(false);
      setOuterLoading(false);
    }
  };

  useEffect(() => {
    setPage(0);
    fetchMembers();
    setSelection([]);
    setAllSelected(false);
  }, [selectedMonth, selectedPod, selectedStatus, refresh]);

  useEffect(() => {
    fetchMembers();
  }, [page, pageSize]);

  useEffect(() => {
    setHasResultsMembers((members?.length || 0) > 0);
  }, [members.length]);

  const updateSelection = (newSelection: number[]) => {
    setSelection(newSelection);
  };

  if (!members.length && !loading) {
    return (
      <PanelInfo
        type="info"
        title={translate('pods.shuffle.results.noResults')}
      />
    );
  }

  const isItemSelected = (podShuffleMemberId: number) => {
    if (allSelected) {
      return !selection.includes(podShuffleMemberId);
    }
    return selection.includes(podShuffleMemberId);
  };

  const allCurrentPageChecked = members.every((m) =>
    isItemSelected(m.podShuffleMemberId),
  );

  const renderStatusColumn = (member: MemberView | PODShuffleMember) => {
    const podShuffleMember = member as PODShuffleMember;
    const status = podShuffleMember.podShuffleStatus;
    if (!status) return EMPTY;

    const color = PODShuffleStatus.getColor(status);
    const statusLabel =
      status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();

    return (
      <Chip
        color={color}
        size="small"
        title={PODShuffleErrorType.byKey[
          podShuffleMember?.podShuffleErrorType ?? ''
        ]?.toString()}
      >
        {statusLabel}
      </Chip>
    );
  };

  return (
    <div className={styles.container}>
      <BulkEditTable
        allChecked={!!(members.length && allCurrentPageChecked)}
        members={members}
        membersSelected={members
          .filter((m) => isItemSelected(m.podShuffleMemberId))
          .map((m) => m.podShuffleMemberId)}
        onChangePage={setPage}
        onChangePageSize={(ps) => {
          setMembers([]);
          setPage(0);
          setPageSize(ps);
          if (pagination) {
            const newPagination = {
              ...pagination,
              limit: ps,
              offset: 0,
            };
            setPagination(newPagination);
            setOuterPagination(newPagination);
          }
        }}
        onClickCheckbox={(member) => {
          const podShuffleMember = member as PODShuffleMember;
          const memberId = podShuffleMember.podShuffleMemberId;

          if (allSelected) {
            updateSelection(
              selection.includes(memberId)
                ? selection.filter((id) => id !== memberId)
                : [...selection, memberId],
            );
          } else {
            updateSelection(
              selection.includes(memberId)
                ? selection.filter((id) => id !== memberId)
                : uniq([...selection, memberId]),
            );
          }
        }}
        onClickCheckboxAll={(checked) => {
          const ids = members.map((m) => m.podShuffleMemberId);
          if (!ids.length) return;

          if (allSelected) {
            if (checked) {
              updateSelection(selection.filter((id) => !ids.includes(id)));
            } else {
              updateSelection(uniq([...selection, ...ids]));
            }
          } else if (checked) {
            updateSelection(selection.filter((id) => !ids.includes(id)));
          } else {
            updateSelection(uniq([...selection, ...ids]));
          }
        }}
        pagination={pagination}
        checkboxField="podShuffleMemberId"
        customColumns={[
          {
            headerName: translate('pods.shuffle.results.status'),
            component: renderStatusColumn,
            width: 120,
          },
        ]}
      />
    </div>
  );
};
